// /admin → Popups: the announcements and sponsored messages the UMaps app shows
// at chosen moments, written here and served signed from GET /api/popups.
//
// Its own file for the reason ChangelogAdmin.js and FinalExamsAdmin.js are:
// this tab PUBLISHES to every phone rather than displaying a table. Laid out
// as —
//
//  · the list (a sidebar) and a workspace for the one that is open;
//  · in the workspace, THE CARD IS THE EDITOR: the popup drawn on a phone at
//    the app's sizes, with the title, body, footer and button typed straight
//    onto it. The body is the same BlockEditor the Event Creator uses (video
//    hidden — the app has no player — and the text style/alignment tools on),
//    restyled to the phone's type under `.pa-card`. Preview swaps in the
//    read-only card, drawn by the rules the app uses (docs/POPUPS.md → "How
//    the image frame is drawn"), with the editing affordances gone;
//  · under the card, every setting that is not ON the card — where, how
//    often, schedule, audience, behaviour — grouped in one container headed
//    by a sentence saying what they add up to, then a save bar pinned to the
//    bottom of the window (⌘S saves; leaving with unsaved edits asks first);
//  · below: Rejected attempts (every refused write, with a red badge until
//    looked at), the audit of accepted writes, and whether the signing key is
//    configured — without it nothing can go live, and the tab says so in red.
//
// Publishing asks for a confirmation that names the consequence, because the
// difference between Save as draft and Publish is the difference between a
// form on this page and a card over ten thousand maps. The contract and the
// limits are docs/POPUPS.md; the server (api/_popups.js) clamps everything the
// editor could get wrong, so this file is a form and not a second validator.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BlockEditor, { withKeys, stripKeys, imgFrameStyle, imgStyle, makeBlock } from './BlockEditor.jsx';
import { FACULTIES, facultyShort } from '../utils/faculties';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';

export const PLACEMENTS = [
  ['launch', 'Launch'],
  ['restaurants', 'Restaurants'],
  ['events', 'Events'],
  ['schedules', 'Schedules'],
  ['courseTools', 'Course Tools'],
  ['roomSchedules', 'Room Schedules'],
  ['settings', 'Settings'],
  ['resources', 'Resources'],
  ['shuttle', 'Shuttle sheet'],
  ['nearby', 'Nearby sheet'],
];
const PLACEMENT_LABEL = Object.fromEntries(PLACEMENTS);

const FREQUENCIES = [
  ['once', 'Once ever'],
  ['daily', 'Once a day'],
  ['every', 'Every N hours'],
  ['always', 'Every time'],
];
const FREQ_LABEL = Object.fromEntries(FREQUENCIES);

/** The block types the app can draw: no video. */
const BLOCK_TYPES = ['text', 'image', 'link'];

/**
 * The newest attempt the operator has looked at, kept in this browser so the
 * tab's red dot means "new since you last opened the panel" rather than
 * "anything has ever been refused". AdminPage reads it for the tab badge;
 * this component writes it when the panel is drawn.
 */
export const ATTEMPTS_SEEN_KEY = 'umaps-popups-attempts-seen';
export function unseenAttempts(attempts) {
  let seen = 0;
  try { seen = Number(localStorage.getItem(ATTEMPTS_SEEN_KEY)) || 0; } catch { /* ignore */ }
  return (attempts || []).filter((a) => Number(a?.at) > seen).length;
}
function markAttemptsSeen(attempts) {
  const newest = Math.max(0, ...(attempts || []).map((a) => Number(a?.at) || 0));
  try { if (newest) localStorage.setItem(ATTEMPTS_SEEN_KEY, String(newest)); } catch { /* ignore */ }
}

const fmtTime = (ms) => {
  if (!ms) return '—';
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? String(ms) : d.toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });
};

/** Epoch ms → the value a <input type="datetime-local"> wants, in local time. */
function toLocalInput(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromLocalInput(s) {
  if (!s) return null;
  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : null;
}

const fmtWindow = (p) => {
  if (!p.startsAt && !p.endsAt) return 'always';
  if (p.startsAt && p.endsAt) return `${fmtTime(p.startsAt)} → ${fmtTime(p.endsAt)}`;
  return p.startsAt ? `from ${fmtTime(p.startsAt)}` : `until ${fmtTime(p.endsAt)}`;
};
const fmtFrequency = (f) => {
  if (!f) return '';
  let s = f.kind === 'every' ? `Every ${f.hours || 24} h` : FREQ_LABEL[f.kind] || f.kind;
  if (f.maxShows) s += ` · at most ${f.maxShows}`;
  if (f.untilInteraction) s += ' · until tapped';
  return s;
};

/** A stored record → the editor's working copy (strings where the form needs them). */
function toDraft(rec) {
  return {
    id: rec.id || '',
    status: rec.status || 'draft',
    rev: rec.rev || 0,
    showRev: rec.showRev || 1,
    title: rec.title || '',
    titleAlign: rec.titleAlign === 'center' ? 'center' : 'left',
    blocks: withKeys(rec.blocks && rec.blocks.length ? rec.blocks : [makeBlock('text')]),
    footerText: rec.footer?.text || '',
    buttonLabel: rec.footer?.button?.label || '',
    buttonUrl: rec.footer?.button?.url || '',
    placements: Array.isArray(rec.placements) && rec.placements.length ? rec.placements : ['launch'],
    frequencyKind: rec.frequency?.kind || 'once',
    frequencyHours: rec.frequency?.hours ? String(rec.frequency.hours) : '24',
    maxShows: rec.frequency?.maxShows ? String(rec.frequency.maxShows) : '',
    untilInteraction: !!rec.frequency?.untilInteraction,
    startsAt: rec.startsAt || null,
    endsAt: rec.endsAt || null,
    platforms: Array.isArray(rec.platforms) && rec.platforms.length ? rec.platforms : ['ios', 'android'],
    minVersion: rec.minVersion || '',
    priority: rec.priority != null ? String(rec.priority) : '0',
    sponsored: !!rec.sponsored,
    dismiss: rec.dismiss === 'button' ? 'button' : 'anywhere',
    faculties: Array.isArray(rec.faculties) ? rec.faculties : [],
  };
}
const blankDraft = () => toDraft({});

/** The working copy → what `op: save` takes (the contract's Popup, minus bookkeeping). */
function toPayload(d, status) {
  const hours = Number(d.frequencyHours);
  const maxShows = Number(d.maxShows);
  const button = d.buttonLabel.trim() && d.buttonUrl.trim() ? { label: d.buttonLabel.trim(), url: d.buttonUrl.trim() } : null;
  return {
    ...(d.id ? { id: d.id } : {}),
    status,
    title: d.title.trim(),
    titleAlign: d.titleAlign,
    blocks: stripKeys(d.blocks),
    footer: d.footerText.trim() || button ? { text: d.footerText.trim() || null, button } : null,
    placements: d.placements,
    frequency: {
      kind: d.frequencyKind,
      ...(d.frequencyKind === 'every' ? { hours: Number.isFinite(hours) ? hours : 24 } : {}),
      maxShows: Number.isFinite(maxShows) && maxShows >= 1 ? Math.round(maxShows) : null,
      ...(d.untilInteraction ? { untilInteraction: true } : {}),
    },
    startsAt: d.startsAt,
    endsAt: d.endsAt,
    platforms: d.platforms,
    minVersion: d.minVersion.trim() || null,
    priority: Number(d.priority) || 0,
    sponsored: d.sponsored,
    dismiss: d.dismiss,
    faculties: d.faculties,
  };
}

/** Something on the card a student can tap: a whole footer button, or a link block. */
export const hasTapTarget = (d) => {
  // https only, as the server keeps them — an http:// button is dropped at save,
  // and with it the only thing that could have stopped the popup.
  const https = (u) => /^https:\/\/[^\s/?#]+/i.test(String(u || '').trim());
  return (!!d.buttonLabel.trim() && https(d.buttonUrl)) || d.blocks.some((b) => b.type === 'link' && https(b.url));
};

/** What the preview draws: the working copy as the phone would see it. */
function previewOf(d) {
  const p = toPayload(d, d.status);
  return { ...p, draft: d.status !== 'live' };
}

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(new Error('Could not read that file.'));
  r.readAsDataURL(file);
});

export default function PopupsAdmin({ token, onExpired, onSeenAttempts }) {
  const [data, setData] = useState(null); // { items, attempts, audit, signing, blobHost }
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [draft, setDraft] = useState(null); // null = nothing open
  const [attemptsOpen, setAttemptsOpen] = useState(false);

  // The parent's callbacks are inline arrows; held in refs so `load` keeps one
  // identity and the effect under it runs once per token, not once per render.
  const expiredRef = useRef(onExpired);
  const seenRef = useRef(onSeenAttempts);
  expiredRef.current = onExpired;
  seenRef.current = onSeenAttempts;

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_BASE}/api/popups?admin=1`, { headers: headers() });
      if (r.status === 401) { expiredRef.current?.(); return; }
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) {
        setData(j);
        markAttemptsSeen(j.attempts);
        seenRef.current?.();
      } else setErr(j.error || `Could not load the popups (${r.status}).`);
    } catch (e) {
      setErr(`Could not load the popups: ${e.message}`);
    } finally {
      setLoaded(true);
    }
  }, [token, headers]);

  useEffect(() => { load(); }, [load]);

  const post = useCallback(async (body, label) => {
    setBusy(label);
    setErr('');
    setNotice('');
    try {
      const r = await fetch(`${API_BASE}/api/popups`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
      if (r.status === 401) { expiredRef.current?.(); return null; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(j.error || `That did not work (${r.status}).`);
        return null;
      }
      await load();
      return j;
    } catch (e) {
      setErr(`That did not work: ${e.message}`);
      return null;
    } finally {
      setBusy('');
    }
  }, [headers, load]);

  const items = data?.items || [];
  const signing = data?.signing || { configured: false };
  const attempts = data?.attempts || [];
  const audit = data?.audit || [];

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const save = async (status) => {
    if (!draft) return;
    if (!draft.title.trim()) { setErr('Give the popup a title.'); return; }
    if (status === 'live' && draft.status !== 'live') {
      if (!window.confirm('Publish this popup? This will show on every phone that qualifies, the next time it checks for announcements.')) return;
    }
    const j = await post({ op: 'save', popup: toPayload(draft, status) }, 'save');
    if (!j) return;
    setDraft(toDraft(j.popup));
    setNotice(status === 'live'
      ? (j.signed ? `Published “${j.popup.title}” (rev ${j.popup.rev}).` : `Saved “${j.popup.title}”, but the feed was NOT re-signed.`)
      : `Saved “${j.popup.title}” as a draft.`);
  };

  const unpublish = async (rec) => {
    const j = await post({ op: 'setStatus', id: rec.id, status: 'draft' }, 'status');
    if (!j) return;
    if (draft?.id === rec.id) setDraft(toDraft(j.popup));
    setNotice(`“${rec.title}” is a draft again; phones drop it on their next check.`);
  };

  const showAgain = async (rec) => {
    if (!window.confirm(`Show “${rec.title}” again to everyone?\n\nA popup set to show once (or once a day) is remembered by each phone once it has been seen. This resets that memory on every phone, so students who already closed it see it one more time. The popup itself is unchanged.`)) return;
    const j = await post({ op: 'showAgain', id: rec.id }, 'showAgain');
    if (!j) return;
    if (draft?.id === rec.id) setDraft(toDraft(j.popup));
    setNotice(`“${rec.title}” will show again to everyone (show revision ${j.popup.showRev}).`);
  };

  const duplicate = (rec) => {
    setErr(''); setNotice('');
    setDraft({ ...toDraft(rec), id: '', status: 'draft', rev: 0, showRev: 1, title: `${rec.title} (copy)`.slice(0, 80) });
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete “${rec.title}”? ${rec.status === 'live' ? 'It is LIVE — phones drop it on their next check. ' : ''}This cannot be undone.`)) return;
    const j = await post({ op: 'delete', id: rec.id }, 'delete');
    if (!j) return;
    if (draft?.id === rec.id) setDraft(null);
    setNotice(`Deleted “${rec.title}”.`);
  };

  const resetCounts = async (rec) => {
    if (!window.confirm(`Reset the view / close / tap counts for “${rec.title}”?`)) return;
    const j = await post({ op: 'resetCounts', id: rec.id }, 'counts');
    if (j) setNotice(`Counts reset for “${rec.title}”.`);
  };

  const clearAttempts = async () => {
    if (!window.confirm('Clear the list of rejected attempts? The e-mails already sent are unaffected.')) return;
    const j = await post({ op: 'clearAttempts' }, 'attempts');
    if (j) setNotice('Rejected attempts cleared.');
  };

  // BlockEditor's upload hook: a file in, our Blob URL out. Throws so the
  // editor can show the server's reason beside the picture well.
  const onUpload = useCallback(async (file) => {
    const dataUrl = await readAsDataUrl(file);
    const r = await fetch(`${API_BASE}/api/popups`, {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ op: 'upload', dataUrl, ...(draft?.id ? { id: draft.id } : {}) }),
    });
    if (r.status === 401) { expiredRef.current?.(); throw new Error('Session expired — log in again.'); }
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.url) throw new Error(j.error || `Upload failed (${r.status}).`);
    return j.url;
  }, [headers, draft?.id]);

  const preview = useMemo(() => (draft ? previewOf(draft) : null), [draft]);
  const unseen = unseenAttempts(attempts);
  const [mode, setMode] = useState('edit'); // the card: 'edit' in place, or 'preview' exactly as drawn

  // Unsaved changes. A saved popup is compared with the copy it was opened (or
  // last saved) as — re-taken whenever the server hands back a new revision —
  // rather than with the stored record, whose server-side normalisation would
  // otherwise read as an edit the moment it was opened. A new one is dirty
  // once it differs from a blank card.
  const baseKey = draft ? `${draft.id}|${draft.rev}|${draft.status}|${draft.showRev}` : '';
  const [base, setBase] = useState({ key: '', json: '' });
  if (base.key !== baseKey) setBase({ key: baseKey, json: draft ? JSON.stringify(toPayload(draft, draft.status)) : '' });
  const nowJson = draft ? JSON.stringify(toPayload(draft, draft.status)) : '';
  const dirty = !!draft && (draft.id ? nowJson !== base.json : nowJson !== BLANK_JSON);

  const leaveOk = () => !dirty || window.confirm('Discard the unsaved changes to this popup?');
  const open = (next) => {
    if (!leaveOk()) return;
    setErr(''); setNotice(''); setMode('edit');
    setDraft(next);
  };

  // ⌘S / Ctrl+S saves a draft (or the changes to a live popup, after its
  // confirm) — the reflex everybody has in a form this long.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!draft) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current(draft.status === 'live' ? 'live' : 'draft');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft]);

  // Closing the tab with an edit in it asks first.
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const stored = draft?.id ? items.find((i) => i.id === draft.id) : null;
  const toggleIn = (list, k) => (list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const buttonHalf = !!draft && !!draft.buttonLabel.trim() !== !!draft.buttonUrl.trim();
  const buttonBadUrl = !!draft && !!draft.buttonUrl.trim() && !/^https:\/\//i.test(draft.buttonUrl.trim());

  return (
    <div className="pa-root">
      <style>{CSS}</style>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={S.top}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={S.topTitle}>Popups</div>
          <div style={S.topNote}>
            Announcements and sponsored messages the app shows at chosen moments: on launch, or when a
            student opens a feature. Phones check one small signed feed on every launch, so a published
            popup reaches them within a few minutes.
          </div>
        </div>
        <div style={S.topRight}>
          <span style={signing.configured ? S.signOk : S.signBad}
                title={signing.configured ? `Public key ${signing.publicKey}` : ''}>
            <span style={{ ...S.signDot, background: signing.configured ? '#1e8e3e' : '#d93025' }} />
            {signing.configured
              ? <>Signing key <span style={S.mono}>{signing.kid}</span> · <span style={S.mono}>{signing.publicKey.slice(0, 4)}…{signing.publicKey.slice(-4)}</span></>
              : 'No signing key: publishing is off'}
          </span>
          <button style={{ ...S.btnPrimary, ...(busy ? S.btnOff : {}) }} disabled={!!busy} onClick={() => open(blankDraft())}>
            + New popup
          </button>
        </div>
      </div>
      {!signing.configured && loaded && (
        <div style={S.err}>Set POPUPS_SIGNING_KEY in Vercel to publish. Drafts can still be saved.</div>
      )}

      {!!err && <div style={S.err}>{err}</div>}
      {!!notice && <div style={S.notice}>{notice}</div>}

      <div style={S.layout}>
        {/* ── The list ───────────────────────────────────────────────────── */}
        <aside style={S.sidebar}>
          <div style={S.sideHead}>
            <span>All popups</span>
            <span style={S.countPill}>{items.length}</span>
          </div>
          {!loaded && <div style={S.emptyNote}>Loading…</div>}
          {loaded && !items.length && <div style={S.emptyNote}>None yet. “New popup” starts one.</div>}
          {items.map((p) => (
            <button key={p.id} className={`pa-item ${draft?.id === p.id ? 'on' : ''}`} onClick={() => draft?.id !== p.id && open(toDraft(p))}>
              <div style={S.itemHead}>
                <span style={{ ...S.statusDot, background: p.status === 'live' ? '#1e8e3e' : '#f9ab00' }} />
                <span style={S.itemTitle}>{p.title}</span>
              </div>
              <div style={S.itemMeta}>
                {p.status === 'live' ? 'Live' : 'Draft'}
                {p.sponsored ? ' · Sponsored' : ''}
                {' · '}{fmtFrequency(p.frequency)}
              </div>
              <div style={S.itemMeta}>{(p.placements || []).map((k) => PLACEMENT_LABEL[k] || k).join(', ')}</div>
              <div style={S.itemStats}>
                <span><b>{p.counts?.view || 0}</b> views</span>
                <span><b>{p.counts?.tap || 0}</b> taps</span>
                <span><b>{p.counts?.dismiss || 0}</b> closed</span>
              </div>
            </button>
          ))}
        </aside>

        {/* ── The workspace ──────────────────────────────────────────────── */}
        <section style={S.workspace}>
          {!draft && (
            <div style={S.blank}>
              <div style={S.blankTitle}>Pick a popup on the left, or start a new one</div>
              <div style={S.blankNote}>The card is the editor: you type straight onto it, sized as the phone draws it.</div>
              <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={() => open(blankDraft())}>+ New popup</button>
            </div>
          )}

          {draft && (
            <>
              {/* What is open, and the things you do TO it rather than IN it. */}
              <div style={S.wsHead}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={S.wsTitleRow}>
                    <span style={{ ...S.chip, ...(draft.status === 'live' ? S.chipLive : S.chipDraft) }}>{draft.status === 'live' ? 'Live' : 'Draft'}</span>
                    <span style={S.wsTitle}>{draft.title || 'Untitled popup'}</span>
                    {dirty && <span style={S.dirty}>Unsaved changes</span>}
                  </div>
                  <div style={S.wsMeta}>
                    {draft.id
                      ? <>rev {draft.rev} · shown as <span style={S.mono}>{draft.id}:{draft.showRev}</span>
                          {stored && <> · {stored.counts?.view || 0} views · {stored.counts?.dismiss || 0} closed · {stored.counts?.tap || 0} button taps · {stored.counts?.link || 0} links · updated {fmtTime(stored.updatedAt)}</>}</>
                      : 'Not saved yet'}
                  </div>
                </div>
                <button style={S.btnGhostSmall} onClick={() => { if (leaveOk()) setDraft(null); }}>Close</button>
              </div>
              {draft.id && (
                <div style={S.wsTools}>
                  {draft.status === 'live'
                    ? <button style={S.btnSmall} disabled={!!busy} onClick={() => unpublish({ id: draft.id, title: draft.title })}>Unpublish</button>
                    : null}
                  <button style={S.btnSmall} disabled={!!busy} onClick={() => showAgain({ id: draft.id, title: draft.title })}
                          title="Phones remember a once / daily popup after it has been seen. This makes every phone show it one more time.">
                    Show again to everyone
                  </button>
                  <button style={S.btnSmall} disabled={!!busy} onClick={() => { if (leaveOk()) duplicate(stored || draft); }}>Duplicate</button>
                  <button style={S.btnSmall} disabled={!!busy} onClick={() => resetCounts({ id: draft.id, title: draft.title })}>Reset counts</button>
                  <button style={S.btnSmallDanger} disabled={!!busy} onClick={() => remove({ id: draft.id, title: draft.title, status: draft.status })}>Delete</button>
                </div>
              )}

              {/* The stage: the card itself, on a phone. */}
              <div style={S.stage}>
                <div style={S.stageBar}>
                  <div className="pa-seg">
                    <button className={mode === 'edit' ? 'on' : ''} onClick={() => setMode('edit')}>Edit</button>
                    <button className={mode === 'preview' ? 'on' : ''} onClick={() => setMode('preview')}>Preview</button>
                  </div>
                  <span style={S.stageHint}>
                    {mode === 'edit'
                      ? 'Click any text to change it. Hover a block for its tools; the + between blocks adds text, a picture or a link.'
                      : 'Exactly as the app draws it: 360 px wide, the card scrolls if it is taller than the screen.'}
                  </span>
                </div>
                {mode === 'edit'
                  ? <EditableCard draft={draft} set={set} onUpload={onUpload}
                                  buttonHalf={buttonHalf} buttonBadUrl={buttonBadUrl} blobHost={data?.blobHost} />
                  : <PhonePreview popup={preview} />}
                <div style={S.stageFoot}>
                  {draft.dismiss === 'button' ? 'Closes only with the ✕.' : 'A tap anywhere closes it.'}
                </div>
              </div>

              {/* The rest of the popup: where, when, who, how. */}
              <div style={S.settings}>
                <div style={S.settingsHead}>
                  <div style={S.settingsTitle}>Settings</div>
                  <div style={S.summary}><Summary d={draft} /></div>
                </div>

                <div style={S.settingsGrid}>
                  <div style={{ ...S.group, gridColumn: '1 / -1' }}>
                    <div style={S.groupTitle}>Where it appears</div>
                    <div style={S.groupNote}>On launch, or the moment a student opens one of these. Pick at least one.</div>
                    <div style={S.chips}>
                      {PLACEMENTS.map(([k, label]) => (
                        <button key={k} type="button" className={`pa-chip ${draft.placements.includes(k) ? 'on' : ''}`}
                                onClick={() => set({ placements: toggleIn(draft.placements, k) })}>
                          {draft.placements.includes(k) ? '✓ ' : ''}{label}
                        </button>
                      ))}
                    </div>
                    {!draft.placements.length && <div style={S.warn}>Pick at least one place, or it can never show.</div>}
                  </div>

                  <div style={S.group}>
                    <div style={S.groupTitle}>How often</div>
                    <div className="pa-seg pa-seg-wrap">
                      {FREQUENCIES.map(([k, label]) => (
                        <button key={k} type="button" className={draft.frequencyKind === k ? 'on' : ''}
                                onClick={() => set({ frequencyKind: k })}>{label}</button>
                      ))}
                    </div>
                    <div style={S.inline}>
                      {draft.frequencyKind === 'every' && (
                        <label style={S.inlineField}>
                          Every
                          <input style={S.inputSmall} type="number" min={1} max={720} value={draft.frequencyHours}
                                 onChange={(e) => set({ frequencyHours: e.target.value })} />
                          hours
                        </label>
                      )}
                      <label style={S.inlineField}>
                        At most
                        <input style={S.inputSmall} type="number" min={1} value={draft.maxShows} placeholder="∞"
                               onChange={(e) => set({ maxShows: e.target.value })} />
                        times per phone
                      </label>
                    </div>
                    <label className="pa-switch" style={{ marginTop: 12 }}>
                      <input type="checkbox" checked={draft.untilInteraction}
                             onChange={(e) => set({ untilInteraction: e.target.checked })} />
                      <span className="pa-knob" />
                      <span>Stop showing after interaction</span>
                    </label>
                    <div style={{ ...S.groupNote, marginTop: 6 }}>
                      Keeps showing on the schedule above until the student taps the button or a link,
                      then never again on that phone. Closing it with the ✕ does not count.
                    </div>
                    {draft.untilInteraction && !hasTapTarget(draft) && (
                      <div style={S.warn}>Add a button or a link block, or there is nothing to tap and this is ignored.</div>
                    )}
                  </div>

                  <div style={S.group}>
                    <div style={S.groupTitle}>Schedule</div>
                    <label style={S.label}>Starts <span style={S.opt}>blank = as soon as it is published</span></label>
                    <input style={S.input} type="datetime-local" value={toLocalInput(draft.startsAt)} onChange={(e) => set({ startsAt: fromLocalInput(e.target.value) })} />
                    <label style={{ ...S.label, marginTop: 10 }}>Ends <span style={S.opt}>blank = never</span></label>
                    <input style={S.input} type="datetime-local" value={toLocalInput(draft.endsAt)} onChange={(e) => set({ endsAt: fromLocalInput(e.target.value) })} />
                    {draft.startsAt && draft.endsAt && draft.endsAt <= draft.startsAt && <div style={S.warn}>It ends before it starts.</div>}
                  </div>

                  <div style={S.group}>
                    <div style={S.groupTitle}>Who sees it</div>
                    <div style={S.chips}>
                      {[['ios', 'iPhone'], ['android', 'Android']].map(([k, label]) => (
                        <button key={k} type="button" className={`pa-chip ${draft.platforms.includes(k) ? 'on' : ''}`}
                                onClick={() => set({ platforms: toggleIn(draft.platforms, k) })}>
                          {draft.platforms.includes(k) ? '✓ ' : ''}{label}
                        </button>
                      ))}
                    </div>
                    {!draft.platforms.length && <div style={S.warn}>Pick at least one platform.</div>}
                    <label style={{ ...S.label, marginTop: 12 }}>Faculty <span style={S.opt}>blank = every student</span></label>
                    <select style={S.input} value=""
                            onChange={(e) => e.target.value && set({ faculties: [...draft.faculties, e.target.value] })}>
                      <option value="">{draft.faculties.length ? '+ Add another faculty' : 'Every faculty'}</option>
                      {FACULTIES.filter((f) => !draft.faculties.includes(f.id)).map((f) => (
                        <option key={f.id} value={f.id}>{f.label}</option>
                      ))}
                    </select>
                    {!!draft.faculties.length && (
                      <div style={{ ...S.chips, marginTop: 8 }}>
                        {draft.faculties.map((id) => (
                          <button key={id} type="button" className="pa-chip on" title="Remove"
                                  onClick={() => set({ faculties: draft.faculties.filter((x) => x !== id) })}>
                            {facultyShort(id) || id} ✕
                          </button>
                        ))}
                      </div>
                    )}
                    {!!draft.faculties.length && (
                      <div style={{ ...S.groupNote, marginTop: 6 }}>
                        Only students who chose one of these in the app's first-run questions. Anyone who skipped the
                        question does not see it. Matched on the phone; the faculty is never sent anywhere.
                      </div>
                    )}
                    <label style={{ ...S.label, marginTop: 12 }}>Minimum app version <span style={S.opt}>e.g. 26.11.0, blank = any</span></label>
                    <input style={S.input} value={draft.minVersion} onChange={(e) => set({ minVersion: e.target.value })} placeholder="Any version" />
                  </div>

                  <div style={S.group}>
                    <div style={S.groupTitle}>Behaviour</div>
                    <label style={S.label}>Closing</label>
                    <div className="pa-seg">
                      <button type="button" className={draft.dismiss === 'anywhere' ? 'on' : ''} onClick={() => set({ dismiss: 'anywhere' })}>Tap anywhere</button>
                      <button type="button" className={draft.dismiss === 'button' ? 'on' : ''} onClick={() => set({ dismiss: 'button' })}>Only the ✕</button>
                    </div>
                    <label className="pa-switch" style={{ marginTop: 12 }}>
                      <input type="checkbox" checked={draft.sponsored} onChange={(e) => set({ sponsored: e.target.checked })} />
                      <span className="pa-knob" />
                      <span>Mark it “Sponsored”</span>
                    </label>
                    <label style={{ ...S.label, marginTop: 12 }}>Priority <span style={S.opt}>higher wins when two qualify at once</span></label>
                    <input style={{ ...S.input, maxWidth: 120 }} type="number" value={draft.priority} onChange={(e) => set({ priority: e.target.value })} />
                  </div>
                </div>
              </div>

              {stored && (
                <PopupAnalytics counts={stored.counts} stats={stored.stats} today={data?.today}
                                live={stored.status === 'live'} onRefresh={load} busy={!!busy} />
              )}

              {/* Save bar, pinned to the bottom of the window while you scroll. */}
              <div style={S.saveBar}>
                <div style={S.saveNote}>
                  {dirty ? <><span style={S.dirtyDot} />Unsaved changes</> : draft.id ? 'All changes saved' : 'Not saved yet'}
                  <span style={S.kbd}>⌘S</span>
                </div>
                <button style={{ ...S.btnGhost, ...(busy ? S.btnOff : {}) }} disabled={!!busy} onClick={() => save('draft')}>
                  {busy === 'save' ? 'Saving…' : draft.status === 'live' ? 'Save as draft (unpublishes)' : 'Save draft'}
                </button>
                <button style={{ ...S.btnPrimary, ...(busy || !signing.configured ? S.btnOff : {}) }} disabled={!!busy || !signing.configured}
                        onClick={() => save('live')} title={signing.configured ? '' : 'The signing key is not configured'}>
                  {busy === 'save' ? 'Saving…' : draft.status === 'live' ? 'Publish changes' : 'Publish'}
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      {/* ── Rejected attempts ────────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardHead}>
          <button style={S.panelToggle} onClick={() => setAttemptsOpen((o) => !o)}>
            {attemptsOpen ? '▾' : '▸'} Rejected attempts ({attempts.length})
            {unseen > 0 && <span style={S.badge}>{unseen} new</span>}
          </button>
          <div style={S.cardHint}>
            Every refused write to /api/popups. You are e-mailed once per reason per 15 minutes.
          </div>
          <button style={{ ...S.btnGhostSmall, ...(attempts.length ? {} : S.btnOff) }} disabled={!attempts.length || !!busy} onClick={clearAttempts}>Clear</button>
        </div>
        {attemptsOpen && !attempts.length && <div style={S.emptyNote}>None. Nothing has tried to write here without the operator's session.</div>}
        {attemptsOpen && attempts.length > 0 && (
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Time', 'IP', 'Reason', 'Op', 'Path', 'Browser'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {attempts.slice(0, 100).map((a, i) => (
                  <tr key={`${a.at}-${i}`}>
                    <td style={S.td}>{fmtTime(a.at)}</td>
                    <td style={S.td}>{a.ip}</td>
                    <td style={S.td}><span style={{ ...S.chip, ...S.chipBad }}>{a.reason}</span>{a.detail ? <span style={S.detail}> {a.detail}</span> : null}</td>
                    <td style={S.td}>{a.op || '—'}</td>
                    <td style={S.td}>{a.path}</td>
                    <td style={{ ...S.td, ...S.tdWrap }}>{a.ua || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {attempts.length > 100 && <div style={S.hint}>…and {attempts.length - 100} more (the list keeps the last 300).</div>}
          </div>
        )}
      </div>

      {/* ── Audit ────────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <div style={S.cardTitle}>Audit ({audit.length})</div>
        {!audit.length && <div style={S.emptyNote}>No writes yet.</div>}
        {audit.slice(0, 60).map((a, i) => (
          <div key={`${a.at}-${i}`} style={S.auditRow}>
            <span style={S.auditTime}>{fmtTime(a.at)}</span>
            <span style={S.auditWho}>{a.who}</span>
            <span style={S.auditOp}>{a.op}</span>
            <span style={S.auditTitle}>{a.title || ''}{a.id ? <span style={S.mono}> {a.id}</span> : null}</span>
          </div>
        ))}
        {audit.length > 60 && <div style={S.hint}>…and {audit.length - 60} more (the list keeps the last 300).</div>}
      </div>
    </div>
  );
}

const BLANK_JSON = JSON.stringify(toPayload(blankDraft(), 'draft'));

// ── Analytics ────────────────────────────────────────────────────────────────
//
// What /api/popup-log counted for one popup: the all-time totals
// (`popups:counts`) and, from the day per-day counting shipped, the same
// events per Winnipeg calendar day plus opens by door and platform
// (`popups:stats:<id>`, api/_popups.js). "Opens" is a `view` — the card was
// drawn on a phone — and "interactions" are the footer button and link
// blocks, the two things somebody can DO with a popup other than close it.
// Counts only: phones with Help improve UMaps switched off send nothing, so
// every number here is a floor, and the panel says so once.

const RANGES = [7, 30, 90];

/** `n` days ending at `today` (YYYY-MM-DD), oldest first. */
function dayList(today, n) {
  const [y, m, d] = String(today || '').split('-').map(Number);
  const end = Number.isFinite(y) ? Date.UTC(y, m - 1, d) : Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const out = [];
  for (let i = n - 1; i >= 0; i--) out.push(new Date(end - i * 86400000).toISOString().slice(0, 10));
  return out;
}
const fmtDay = (iso, opts = { month: 'short', day: 'numeric' }) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-CA', { ...opts, timeZone: 'UTC' });
const pctOf = (a, b) => (b > 0 ? `${Math.round((a / b) * 1000) / 10}%` : '—');

function PopupAnalytics({ counts, stats, today, live, onRefresh, busy }) {
  const [range, setRange] = useState(30);
  const c = counts || { view: 0, dismiss: 0, tap: 0, link: 0 };
  const st = stats || { days: {}, placements: {}, platforms: {} };
  const days = dayList(today, range);
  const rows = days.map((d) => ({ day: d, ...(st.days[d] || { view: 0, dismiss: 0, tap: 0, link: 0 }) }));
  const inRange = rows.reduce((a, r) => ({ view: a.view + r.view, act: a.act + r.tap + r.link, dismiss: a.dismiss + r.dismiss }), { view: 0, act: 0, dismiss: 0 });
  const max = Math.max(1, ...rows.map((r) => r.view));
  const interactions = c.tap + c.link;

  const counted = Object.keys(st.days).sort();
  const dayViews = counted.reduce((n, d) => n + (st.days[d].view || 0), 0);
  const earlier = c.view - dayViews;
  const best = rows.reduce((b, r) => (r.view > (b?.view || 0) ? r : b), null);

  const placements = Object.entries(st.placements).sort((a, b) => b[1] - a[1]);
  const placeMax = Math.max(1, ...placements.map(([, n]) => n));
  const ios = st.platforms.ios || 0, android = st.platforms.android || 0;

  // The chart: one column per day, opens as the bar, interactions as the darker
  // part of it. Drawn in SVG units and stretched to the box.
  const W = 700, H = 160, gap = range > 60 ? 1 : 2;
  const bw = W / rows.length;

  return (
    <div style={S.settings}>
      <div style={S.anHead}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={S.settingsTitle}>Analytics</div>
          <div style={{ ...S.groupNote, marginTop: 4, marginBottom: 0 }}>
            {live ? 'Counted as phones report it.' : 'A draft is only seen on development builds, so these are mostly test views.'}{' '}
            Phones with “Help improve UMaps” off send nothing, so every number is a floor.
          </div>
        </div>
        <button style={S.btnGhostSmall} disabled={busy} onClick={onRefresh}>Refresh</button>
      </div>

      <div style={S.tiles}>
        <Tile label="Opens" value={c.view} note="times it was shown" strong />
        <Tile label="Interactions" value={interactions} note={`${c.tap} button · ${c.link} link`} strong />
        <Tile label="Interaction rate" value={pctOf(interactions, c.view)} note="interactions per open" />
        <Tile label="Closed" value={c.dismiss} note={`${pctOf(c.dismiss, c.view)} of opens`} />
      </div>

      <div style={S.chartHead}>
        <div>
          <div style={S.groupTitle}>Opens per day</div>
          <div style={S.chartNote}>
            {inRange.view} opens · {inRange.act} interactions in the last {range} days
            {inRange.view ? <> · {Math.round((inRange.view / range) * 10) / 10} a day on average</> : null}
            {best && best.view ? <> · busiest {fmtDay(best.day)} ({best.view})</> : null}
          </div>
        </div>
        <div className="pa-seg">
          {RANGES.map((n) => (
            <button key={n} type="button" className={range === n ? 'on' : ''} onClick={() => setRange(n)}>{n} days</button>
          ))}
        </div>
      </div>
      <div style={S.chartBox}>
        <div style={S.chartMax}>{max}</div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
          <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#dadce0" strokeWidth="1" />
          <line x1="0" y1="0.5" x2={W} y2="0.5" stroke="#f1f3f4" strokeWidth="1" />
          {rows.map((r, i) => {
            const h = (r.view / max) * (H - 4);
            const ha = Math.min(h, ((r.tap + r.link) / max) * (H - 4));
            const x = i * bw + gap / 2;
            return (
              <g key={r.day}>
                <title>{`${fmtDay(r.day, { weekday: 'short', month: 'short', day: 'numeric' })}: ${r.view} opens, ${r.tap + r.link} interactions, ${r.dismiss} closed`}</title>
                <rect x={i * bw} y="0" width={bw} height={H} fill="transparent" />
                {r.view > 0 && <rect x={x} y={H - h} width={Math.max(1, bw - gap)} height={h} rx="2" fill="#8ab4f8" />}
                {ha > 0 && <rect x={x} y={H - ha} width={Math.max(1, bw - gap)} height={ha} rx="2" fill="#1a73e8" />}
              </g>
            );
          })}
        </svg>
        <div style={S.chartAxis}>
          <span>{fmtDay(days[0])}</span>
          <span>{fmtDay(days[Math.floor(days.length / 2)])}</span>
          <span>{fmtDay(days[days.length - 1])}</span>
        </div>
        <div style={S.legend}>
          <span><i style={{ ...S.swatch, background: '#8ab4f8' }} />Opens</span>
          <span><i style={{ ...S.swatch, background: '#1a73e8' }} />Interactions</span>
          <span style={{ color: '#80868b' }}>Hover a day for its numbers.</span>
        </div>
      </div>
      {earlier > 0 && (
        <div style={S.hint}>
          Day-by-day counting started {counted.length ? fmtDay(counted[0], { month: 'long', day: 'numeric', year: 'numeric' }) : 'today'};
          {' '}{earlier} earlier {earlier === 1 ? 'open is' : 'opens are'} in the totals only.
        </div>
      )}

      <div style={S.breakGrid}>
        <div style={S.group}>
          <div style={S.groupTitle}>Opens by where it appeared</div>
          {!placements.length && <div style={S.emptyNote}>No opens counted by place yet.</div>}
          {placements.map(([k, n]) => (
            <div key={k} style={S.barRow}>
              <span style={S.barLabel}>{PLACEMENT_LABEL[k] || k}</span>
              <span style={S.barTrack}><span style={{ ...S.barFill, width: `${(n / placeMax) * 100}%` }} /></span>
              <span style={S.barNum}>{n}</span>
            </div>
          ))}
        </div>
        <div style={S.group}>
          <div style={S.groupTitle}>Opens by platform</div>
          {!(ios + android) && <div style={S.emptyNote}>No opens counted by platform yet.</div>}
          {ios + android > 0 && [['iPhone', ios], ['Android', android]].map(([label, n]) => (
            <div key={label} style={S.barRow}>
              <span style={S.barLabel}>{label}</span>
              <span style={S.barTrack}><span style={{ ...S.barFill, width: `${(n / Math.max(ios, android, 1)) * 100}%` }} /></span>
              <span style={S.barNum}>{n} <span style={{ color: '#80868b' }}>({pctOf(n, ios + android)})</span></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, note, strong }) {
  return (
    <div style={{ ...S.tile, ...(strong ? S.tileStrong : {}) }}>
      <div style={S.tileLabel}>{label}</div>
      <div style={S.tileValue}>{typeof value === 'number' ? value.toLocaleString('en-CA') : value}</div>
      <div style={S.tileNote}>{note}</div>
    </div>
  );
}

/** The settings in one sentence, so a mistake reads as a wrong sentence. */
function Summary({ d }) {
  const where = d.placements.map((k) => PLACEMENT_LABEL[k] || k);
  const how = d.frequencyKind === 'every' ? `every ${Number(d.frequencyHours) || 24} hours`
    : { once: 'once ever', daily: 'once a day', always: 'every time' }[d.frequencyKind] || d.frequencyKind;
  const cap = Number(d.maxShows) >= 1 ? `, at most ${Math.round(Number(d.maxShows))} times` : '';
  const who = d.platforms.length === 2 ? 'iPhone and Android' : d.platforms[0] === 'ios' ? 'iPhone only' : d.platforms[0] === 'android' ? 'Android only' : 'no platform';
  const win = d.startsAt || d.endsAt ? fmtWindow(d) : null;
  const facs = d.faculties.map((id) => facultyShort(id) || id);
  return (
    <>
      Shows {where.length ? <>on <b>{where.join(', ')}</b></> : <b style={{ color: '#c5221f' }}>nowhere yet</b>},{' '}
      <b>{how}</b>{cap}, on <b>{who}</b>{d.minVersion.trim() ? <> (app {d.minVersion.trim()}+)</> : null}
      {facs.length ? <>, to <b>{facs.join(', ')}</b> students only</> : null}
      {win ? <>, <b>{win}</b></> : null}
      {d.untilInteraction && hasTapTarget(d) ? <>, until they tap it</> : null}.
    </>
  );
}

/** A one-line field that wraps like the title it stands in for; Enter does nothing. */
function GrowText({ value, onChange, placeholder, maxLength, className }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea ref={ref} rows={1} className={className} value={value} placeholder={placeholder} maxLength={maxLength}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))} />
  );
}

/**
 * The card as an editor: the same frame, card and type sizes as PhonePreview,
 * with every part typed into where it sits. The body is BlockEditor restyled
 * (CSS below, under `.pa-card`) to the phone's sizes, so the only things here
 * that the phone does not draw are the editing affordances themselves — the
 * dashed outlines, the block tools and the + rows — which Preview takes away.
 */
function EditableCard({ draft, set, onUpload, buttonHalf, buttonBadUrl, blobHost }) {
  const hasButton = !!draft.buttonLabel.trim() || !!draft.buttonUrl.trim();
  const centred = draft.titleAlign === 'center';
  return (
    <div style={{ ...P.phone, ...P.phoneGrow }}>
      <div style={{ ...P.mapHint, borderRadius: 28 }} />
      <div style={{ ...P.scrim, ...P.scrimGrow }}>
        <div className="pa-card" style={{ ...P.card, ...P.cardGrow }}>
          <div className="pa-titlerow" style={P.head}>
            {centred && <span style={P.closeSpace} />}
            <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
              <div className="pa-talign">
                <button type="button" className={centred ? '' : 'on'} onClick={() => set({ titleAlign: 'left' })}>Left</button>
                <button type="button" className={centred ? 'on' : ''} onClick={() => set({ titleAlign: 'center' })}>Centre</button>
              </div>
              <GrowText className={`pa-edit pa-title${centred ? ' pa-center' : ''}`} value={draft.title} maxLength={80} placeholder="Title"
                        onChange={(v) => set({ title: v })} />
            </div>
            <span style={P.close} aria-hidden>✕</span>
          </div>
          {draft.title.length > 64 && <div style={P.counter}>{draft.title.length}/80</div>}
          <Tags sponsored={draft.sponsored} draft={draft.status !== 'live'} centred={centred} />

          <div className="pa-body">
            <BlockEditor blocks={draft.blocks} onChange={(b) => set({ blocks: b.slice(0, 12) })} onUpload={onUpload} types={BLOCK_TYPES} textTools />
          </div>

          <GrowText className="pa-edit pa-foot" value={draft.footerText} maxLength={160} placeholder="Footer line (optional, small and grey)"
                    onChange={(v) => set({ footerText: v })} />

          <div className={`pa-btn ${hasButton ? '' : 'empty'}`}>
            <input value={draft.buttonLabel} maxLength={24} placeholder={hasButton ? 'Button label' : '+ Add a button (optional)'}
                   onChange={(e) => set({ buttonLabel: e.target.value })} />
          </div>
          {hasButton && (
            <div className={`pa-url ${buttonHalf || buttonBadUrl ? 'bad' : ''}`}>
              <span>Opens</span>
              <input value={draft.buttonUrl} placeholder="https://…" onChange={(e) => set({ buttonUrl: e.target.value })} />
            </div>
          )}
          {buttonHalf && <div style={P.warnSmall}>A button needs both a label and a link, or it is left off.</div>}
          {!buttonHalf && buttonBadUrl && <div style={P.warnSmall}>The link must start with https://</div>}
        </div>
      </div>
      <div style={P.uploadNote}>
        Pictures are shrunk to 1200 px and stored under <span style={S.mono}>popups/</span>
        {blobHost ? <> on <span style={S.mono}>{blobHost}</span></> : null}. Drag a picture's edges to crop it.
      </div>
    </div>
  );
}

/** "Sponsored" / "DRAFT" under the title, on the title's alignment; nothing when neither. */
function Tags({ sponsored, draft, centred }) {
  if (!sponsored && !draft) return null;
  return (
    <div style={{ ...P.tags, justifyContent: centred ? 'center' : 'flex-start' }}>
      {sponsored && <span style={P.tagSponsored}>Sponsored</span>}
      {draft && <span style={P.tagDraft}>DRAFT</span>}
    </div>
  );
}

/**
 * The card as the phone draws it, 360 px wide: scrim, white card with a 16 px
 * radius, the title bold at 17 on the ✕'s line (centred against a spacer the
 * ✕'s width when asked, and stationary: only the body scrolls, as in
 * PopupCard.tsx), the tags under it, text blocks at 15 (small 13 muted, heading 16
 * bold, aligned as set), images through `imgFrameStyle` / `imgStyle` — the
 * same rule the app's imageFrame.ts follows — link blocks as a tinted pill, a
 * muted 12.5 footer line and a filled #1a73e8 button, pinned under the body.
 */
function PhonePreview({ popup }) {
  const blocks = (popup.blocks || []).filter((b) => b && b.type);
  const centred = popup.titleAlign === 'center';
  return (
    <div style={P.phone}>
      <div style={P.mapHint} />
      <div style={P.scrim}>
        <div style={{ ...P.card, ...P.cardFixed }}>
          <div style={{ ...P.head, padding: '18px 18px 0' }}>
            {centred && <span style={P.closeSpace} />}
            <div style={{ ...P.title, textAlign: centred ? 'center' : 'left' }}>{popup.title || <span style={P.placeholder}>Title</span>}</div>
            <span style={P.close} aria-label="Close">✕</span>
          </div>
          <div style={{ padding: '0 18px' }}><Tags sponsored={popup.sponsored} draft={popup.draft} centred={centred} /></div>
          <div style={P.scrollBody}>
          {blocks.map((b, i) => {
            if (b.type === 'text') {
              if (!b.text) return null;
              const st = b.style === 'heading' ? P.heading : b.style === 'small' ? P.small : P.body;
              return (
                <div key={i} style={{ ...st, textAlign: b.align === 'center' ? 'center' : 'left' }}>
                  {String(b.text).split(/\n{2,}/).map((para, k) => <p key={k} style={P.para}>{para}</p>)}
                </div>
              );
            }
            if (b.type === 'image') {
              if (!b.url) return <div key={i} style={P.imgEmpty}>Picture (not uploaded yet)</div>;
              return (
                <figure key={i} style={{ ...P.fig, ...(imgFrameStyle(b) || {}) }}>
                  <img src={b.url} alt={b.alt || b.caption || ''} style={{ ...P.img, ...(imgStyle(b) || {}) }} />
                  {b.caption && <figcaption style={P.caption}>{b.caption}</figcaption>}
                </figure>
              );
            }
            if (b.type === 'link') {
              return <div key={i} style={P.link}>{b.text || b.url || 'Link'}</div>;
            }
            return null;
          })}
          </div>
          {(popup.footer?.text || popup.footer?.button) && (
            <div style={P.footer}>
              {popup.footer?.text && <div style={P.footerText}>{popup.footer.text}</div>}
              {popup.footer?.button && <div style={P.button}>{popup.footer.button.label}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FONT = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif';

const P = {
  phone: { position: 'relative', width: 360, maxWidth: '100%', height: 640, borderRadius: 28, overflow: 'hidden', background: '#dfe7ea', boxShadow: '0 0 0 6px #202124, 0 18px 40px rgba(0,0,0,0.28)', margin: '8px auto' },
  // Edit mode grows with the card instead of scrolling inside it — the block
  // tools and the + menus need room to open, and a scroll box would clip them.
  phoneGrow: { height: 'auto', minHeight: 640, overflow: 'visible', display: 'flex', flexDirection: 'column' },
  mapHint: { position: 'absolute', inset: 0, background: 'repeating-linear-gradient(45deg, #d6e2e6 0 18px, #dfe7ea 18px 36px)' },
  scrim: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, boxSizing: 'border-box' },
  scrimGrow: { position: 'relative', inset: 'auto', flex: 1, minHeight: 640, borderRadius: 28, padding: '40px 18px' },
  card: { position: 'relative', width: '100%', maxHeight: '100%', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: '18px 18px 16px', boxSizing: 'border-box', color: '#17202a', fontFamily: FONT, display: 'flex', flexDirection: 'column', gap: 10 },
  cardGrow: { maxHeight: 'none', overflow: 'visible', gap: 8 },
  head: { display: 'flex', alignItems: 'flex-start', gap: 8 },
  closeSpace: { width: 24, flex: 'none' },
  tags: { display: 'flex', gap: 6, marginTop: -2 },
  cardFixed: { padding: 0, overflow: 'hidden', gap: 8 },
  scrollBody: { overflowY: 'auto', minHeight: 0, flexShrink: 1, padding: '2px 18px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  footer: { borderTop: '1px solid #e8eaed', padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 10 },
  tagSponsored: { fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#5f6368', background: '#f1f3f4', borderRadius: 999, padding: '2px 7px' },
  tagDraft: { fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, color: '#7a5a00', background: '#fef7e0', borderRadius: 999, padding: '2px 7px' },
  close: { flex: 'none', marginTop: -1, width: 24, height: 24, borderRadius: 12, background: '#f1f3f4', color: '#5f6368', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 },
  title: { flex: 1, minWidth: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.3 },
  placeholder: { color: '#9aa0a6' },
  counter: { fontSize: 11, color: '#9aa0a6', marginTop: -6, textAlign: 'right' },
  body: { fontSize: 15, lineHeight: 1.5 },
  small: { fontSize: 13, lineHeight: 1.45, color: '#5f6368' },
  heading: { fontSize: 16, fontWeight: 700, lineHeight: 1.35 },
  para: { margin: 0 },
  fig: { margin: 0 },
  img: { width: '100%', display: 'block', borderRadius: 10, background: '#f1f3f4' },
  imgEmpty: { height: 120, borderRadius: 10, border: '2px dashed #dadce0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa0a6', fontSize: 12.5 },
  caption: { marginTop: 6, fontSize: 12.5, color: '#5f6368' },
  link: { textAlign: 'center', background: '#e8f0fe', color: '#1a5fb4', borderRadius: 10, padding: '11px 14px', fontWeight: 700, fontSize: 15 },
  footerText: { fontSize: 12.5, color: '#5f6368', lineHeight: 1.4 },
  button: { textAlign: 'center', background: '#1a73e8', color: '#fff', borderRadius: 10, padding: '12px 14px', fontWeight: 800, fontSize: 15 },
  warnSmall: { fontSize: 11.5, color: '#c5221f', fontWeight: 600, marginTop: -2 },
  uploadNote: { position: 'absolute', left: 0, right: 0, bottom: -42, fontSize: 11.5, color: '#80868b', textAlign: 'center' },
};

// The editing skin. BlockEditor carries its own stylesheet; everything under
// `.pa-card` re-sizes it to the phone's type so the edit view and Preview
// differ only by the editing affordances.
const CSS = `
.pa-card .be-root { --accent:#1a73e8; --ink:#17202a; --dim:#5f6368; --line:#dadce0; }
.pa-card .be-div { height:10px; }
.pa-card .be-div.open { height:26px; }
.pa-card .be-text { font-family:${FONT}; font-size:15px; line-height:1.5; padding:3px 6px; margin:0 -6px; width:calc(100% + 12px); }
.pa-card .be-text::placeholder { color:#9aa0a6; }
.pa-card .be-text-heading { font-size:16px; font-weight:700; line-height:1.35; }
.pa-card .be-text-small { font-size:13px; line-height:1.45; color:#5f6368; }
.pa-card .be-block:hover .be-text { border-color:#e3e6ea; }
.pa-card .be-text:focus { border-color:#1a73e8; }
.pa-card .be-img { border-radius:10px; }
.pa-card .be-linkbox { background:#e8f0fe; border-radius:10px; padding:11px 14px; }
.pa-card .be-linkbox input { color:#1a5fb4; font-family:${FONT}; font-weight:700; font-size:15px; }
.pa-card .be-ttools { position:absolute; top:-30px; left:22px; z-index:6; background:#fff; padding:3px;
  border-radius:8px; box-shadow:0 3px 12px rgba(0,0,0,.18); opacity:0; pointer-events:none; transition:opacity 120ms; }
.pa-card .be-block.hot .be-ttools, .pa-card .be-block:focus-within .be-ttools { opacity:1; pointer-events:auto; }
/* The block tools and the crop grips appear on hover only, so a resting card
   reads as the card. BlockEditor dims a disabled arrow with !important, which
   would otherwise leave it showing at 30% on every block. */
.pa-card .be-block:not(.hot) .be-corner { opacity:0 !important; pointer-events:none; }
.pa-card .be-grip, .pa-card .be-cropbadge { opacity:0; transition:opacity 120ms; }
.pa-card .be-block.hot .be-grip, .pa-card .be-block.hot .be-cropbadge { opacity:1; }

.pa-edit { display:block; width:calc(100% + 12px); margin:0 -6px; box-sizing:border-box; resize:none; overflow:hidden;
  border:1px dashed transparent; border-radius:6px; background:transparent; padding:2px 6px; color:inherit;
  font-family:${FONT}; transition:border-color 120ms, background 120ms; }
.pa-card:hover .pa-edit { border-color:#e3e6ea; }
.pa-edit:hover { border-color:#c4c9cf !important; }
.pa-edit:focus { outline:none; border-style:solid; border-color:#1a73e8 !important; background:#fff; }
.pa-edit::placeholder { color:#9aa0a6; }
.pa-title { font-size:17px; font-weight:700; line-height:1.3; }
.pa-center { text-align:center; }
/* Left / Centre for the title: shown on hover or while typing, like a text block's tools. */
.pa-talign { position:absolute; top:-30px; left:50%; transform:translateX(-50%); z-index:6; display:flex; gap:3px;
  background:#fff; padding:3px; border-radius:8px; box-shadow:0 3px 12px rgba(0,0,0,.18); opacity:0; pointer-events:none;
  transition:opacity 120ms; }
.pa-titlerow:hover .pa-talign, .pa-titlerow:focus-within .pa-talign { opacity:1; pointer-events:auto; }
.pa-talign button { border:1px solid #dadce0; background:#fff; color:#41474f; border-radius:6px; padding:2px 8px;
  font:inherit; font-size:11px; font-weight:700; cursor:pointer; }
.pa-talign button.on { border-color:#1a73e8; background:#1a73e8; color:#fff; }
.pa-foot { font-size:12.5px; line-height:1.4; color:#5f6368; }

.pa-btn { border-radius:10px; background:#1a73e8; padding:0 14px; border:1.5px solid #1a73e8; transition:background 120ms; }
.pa-btn input { width:100%; box-sizing:border-box; border:none; outline:none; background:transparent; text-align:center;
  color:#fff; font-family:${FONT}; font-weight:800; font-size:15px; padding:11px 0; }
.pa-btn input::placeholder { color:rgba(255,255,255,.7); }
.pa-btn.empty { background:transparent; border-style:dashed; border-color:#a8c7fa; }
.pa-btn.empty:hover { background:#f4f8fe; }
.pa-btn.empty input { color:#1a73e8; font-weight:700; }
.pa-btn.empty input::placeholder { color:#1a73e8; }
.pa-btn:focus-within { box-shadow:0 0 0 3px rgba(26,115,232,.25); }
.pa-url { display:flex; align-items:center; gap:6px; background:#f8f9fa; border:1px solid #e3e6ea; border-radius:8px;
  padding:4px 4px 4px 9px; margin-top:-2px; }
.pa-url span { font-size:11px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color:#80868b; }
.pa-url input { flex:1; min-width:0; border:none; outline:none; background:transparent; font-family:ui-monospace,SFMono-Regular,Menlo,monospace;
  font-size:12px; color:#202124; padding:4px 2px; }
.pa-url.bad { border-color:#f3b8b4; background:#fef6f5; }
.pa-url:focus-within { border-color:#1a73e8; background:#fff; }

.pa-item { display:block; width:100%; text-align:left; background:#fff; border:1.5px solid transparent; border-radius:10px;
  padding:10px 11px; margin-bottom:6px; cursor:pointer; font:inherit; box-shadow:0 1px 3px rgba(0,0,0,.06);
  transition:border-color 120ms, box-shadow 120ms; }
.pa-item:hover { border-color:#dadce0; box-shadow:0 2px 8px rgba(0,0,0,.08); }
.pa-item.on { border-color:#1a73e8; background:#f4f8fe; }

.pa-seg { display:inline-flex; background:#eceff1; border-radius:9px; padding:3px; gap:2px; }
.pa-seg-wrap { display:grid; grid-template-columns:1fr 1fr; }
.pa-seg button { border:none; background:transparent; border-radius:7px; padding:6px 12px; font:inherit; font-size:13px;
  font-weight:700; color:#5f6368; cursor:pointer; white-space:nowrap; }
.pa-seg button:hover { color:#202124; }
.pa-seg button.on { background:#fff; color:#1a73e8; box-shadow:0 1px 3px rgba(0,0,0,.14); }

.pa-chip { border:1.5px solid #dadce0; background:#fff; color:#3c4043; border-radius:999px; padding:6px 12px;
  font:inherit; font-size:13px; font-weight:700; cursor:pointer; transition:all 120ms; }
.pa-chip:hover { border-color:#a8c7fa; }
.pa-chip.on { background:#e8f0fe; border-color:#1a73e8; color:#1a5fb4; }

.pa-switch { display:flex; align-items:center; gap:10px; cursor:pointer; font-size:13.5px; font-weight:600; color:#202124; }
.pa-switch input { position:absolute; opacity:0; pointer-events:none; }
.pa-knob { position:relative; width:36px; height:20px; border-radius:10px; background:#dadce0; flex:none; transition:background 150ms; }
.pa-knob::after { content:''; position:absolute; top:2px; left:2px; width:16px; height:16px; border-radius:8px; background:#fff;
  box-shadow:0 1px 3px rgba(0,0,0,.3); transition:transform 150ms; }
.pa-switch input:checked + .pa-knob { background:#1a73e8; }
.pa-switch input:checked + .pa-knob::after { transform:translateX(16px); }
.pa-switch input:focus-visible + .pa-knob { box-shadow:0 0 0 3px rgba(26,115,232,.3); }
`;

const S = {
  top: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
  topTitle: { fontSize: 20, fontWeight: 800, color: '#202124' },
  topNote: { fontSize: 13, color: '#5f6368', marginTop: 4, maxWidth: 560, lineHeight: 1.5 },
  topRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  signOk: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#137333', fontWeight: 700, background: '#e6f4ea', borderRadius: 999, padding: '5px 11px' },
  signBad: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#c5221f', fontWeight: 700, background: '#fce8e6', borderRadius: 999, padding: '5px 11px' },
  signDot: { width: 7, height: 7, borderRadius: 4, display: 'inline-block' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10 },
  notice: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10 },

  layout: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 16 },
  sidebar: { flex: '0 1 250px', minWidth: 220, background: '#f5f7f9', borderRadius: 12, padding: 10, boxSizing: 'border-box' },
  sideHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#5f6368', padding: '2px 4px 10px' },
  countPill: { background: '#e3e6ea', color: '#3c4043', borderRadius: 999, padding: '1px 8px', fontSize: 11, letterSpacing: 0 },
  itemHead: { display: 'flex', alignItems: 'center', gap: 7 },
  itemTitle: { fontSize: 13.5, fontWeight: 800, color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 },
  statusDot: { width: 8, height: 8, borderRadius: 4, flex: 'none' },
  itemMeta: { fontSize: 11.5, color: '#5f6368', marginTop: 3, lineHeight: 1.4, paddingLeft: 15 },
  itemStats: { display: 'flex', gap: 10, fontSize: 11, color: '#80868b', marginTop: 6, paddingLeft: 15 },

  workspace: { flex: '1 1 520px', minWidth: 0 },
  blank: { border: '2px dashed #dadce0', borderRadius: 14, padding: '56px 24px', textAlign: 'center', background: '#fafbfc' },
  blankTitle: { fontSize: 16, fontWeight: 800, color: '#202124' },
  blankNote: { fontSize: 13, color: '#5f6368', marginTop: 6 },

  wsHead: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 },
  wsTitleRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  wsTitle: { fontSize: 16, fontWeight: 800, color: '#202124' },
  wsMeta: { fontSize: 12, color: '#5f6368', marginTop: 4, lineHeight: 1.45 },
  wsTools: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 },
  dirty: { fontSize: 11, fontWeight: 700, color: '#b06000', background: '#fef7e0', borderRadius: 999, padding: '2px 8px' },

  stage: { background: 'radial-gradient(circle at 1px 1px, #d5dbe1 1px, transparent 0) 0 0 / 18px 18px, #eef1f4', borderRadius: 14, padding: '14px 14px 52px', marginBottom: 14 },
  stageBar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
  stageHint: { fontSize: 12, color: '#5f6368', flex: 1, minWidth: 200, lineHeight: 1.4 },
  stageFoot: { fontSize: 12, color: '#5f6368', textAlign: 'center', marginTop: 52 },

  settings: { background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 16, marginBottom: 12 },
  settingsHead: { marginBottom: 12 },
  settingsTitle: { fontSize: 15, fontWeight: 800, color: '#202124' },
  summary: { fontSize: 13, color: '#3c4043', marginTop: 6, lineHeight: 1.5, background: '#f4f8fe', border: '1px solid #d2e3fc', borderRadius: 8, padding: '8px 11px' },
  settingsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 },
  group: { border: '1px solid #e8eaed', borderRadius: 10, padding: '12px 13px' },
  groupTitle: { fontSize: 13, fontWeight: 800, color: '#202124', marginBottom: 8 },
  groupNote: { fontSize: 12, color: '#5f6368', marginTop: -4, marginBottom: 10 },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  inline: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 },
  inlineField: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#3c4043' },
  inputSmall: { width: 72, boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 7, padding: '6px 8px', font: 'inherit', fontSize: 13.5 },
  warn: { fontSize: 12, color: '#c5221f', fontWeight: 600, marginTop: 8 },

  saveBar: { position: 'sticky', bottom: 12, zIndex: 20, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(6px)', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.16)', padding: '10px 12px' },
  saveNote: { flex: 1, minWidth: 140, fontSize: 12.5, color: '#5f6368', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7 },
  dirtyDot: { width: 8, height: 8, borderRadius: 4, background: '#f9ab00', display: 'inline-block' },
  kbd: { fontSize: 10.5, fontWeight: 700, color: '#80868b', border: '1px solid #dadce0', borderRadius: 5, padding: '0 5px', marginLeft: 4 },

  anHead: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 },
  tile: { border: '1px solid #e8eaed', borderRadius: 10, padding: '10px 12px' },
  tileStrong: { background: '#f4f8fe', borderColor: '#d2e3fc' },
  tileLabel: { fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368' },
  tileValue: { fontSize: 24, fontWeight: 800, color: '#202124', marginTop: 4, lineHeight: 1.1 },
  tileNote: { fontSize: 11.5, color: '#80868b', marginTop: 3 },
  chartHead: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 },
  chartNote: { fontSize: 12, color: '#5f6368', marginTop: -4 },
  chartBox: { position: 'relative', border: '1px solid #e8eaed', borderRadius: 10, padding: '12px 12px 8px' },
  chartMax: { position: 'absolute', top: 4, left: 14, fontSize: 10.5, color: '#9aa0a6', fontWeight: 700 },
  chartAxis: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#80868b', marginTop: 4 },
  legend: { display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: '#3c4043', marginTop: 8 },
  swatch: { display: 'inline-block', width: 10, height: 10, borderRadius: 3, marginRight: 5, verticalAlign: -1 },
  breakGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginTop: 14 },
  barRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginTop: 6 },
  barLabel: { width: 110, flex: 'none', color: '#3c4043', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { flex: 1, height: 8, background: '#f1f3f4', borderRadius: 4, overflow: 'hidden' },
  barFill: { display: 'block', height: '100%', background: '#1a73e8', borderRadius: 4 },
  barNum: { flex: 'none', minWidth: 34, textAlign: 'right', fontWeight: 700, color: '#202124' },

  card: { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: 16, marginBottom: 14 },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  cardTitle: { fontSize: 13, fontWeight: 800, color: '#202124', marginBottom: 10 },
  cardHint: { fontSize: 12, color: '#5f6368', flex: 1, minWidth: 160, marginBottom: 10 },
  emptyNote: { fontSize: 13, color: '#80868b', lineHeight: 1.45, margin: '0 4px 8px' },
  hint: { fontSize: 12.5, color: '#5f6368', lineHeight: 1.5, marginTop: 8 },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 },

  chip: { fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' },
  chipLive: { background: '#e6f4ea', color: '#137333' },
  chipDraft: { background: '#fef7e0', color: '#7a5a00' },
  chipBad: { background: '#fce8e6', color: '#c5221f', textTransform: 'none' },
  detail: { fontSize: 11.5, color: '#5f6368' },

  label: { display: 'block', fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: '#5f6368', marginBottom: 5 },
  opt: { fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: '#9aa0a6' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #dadce0', borderRadius: 8, padding: '8px 10px', font: 'inherit', fontSize: 14, background: '#fff' },

  btnPrimary: { background: '#1a73e8', color: '#fff', border: '1.5px solid #1a73e8', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnGhost: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  btnGhostSmall: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 8, padding: '5px 10px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  btnSmall: { background: '#fff', color: '#202124', border: '1px solid #dadce0', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  btnSmallDanger: { background: '#fff', color: '#c5221f', border: '1px solid #f3b8b4', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  btnOff: { background: '#f1f3f4', color: '#9aa0a6', border: '1.5px solid #f1f3f4', cursor: 'default' },

  panelToggle: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 13, fontWeight: 800, color: '#202124', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 },
  badge: { fontSize: 10.5, fontWeight: 800, color: '#fff', background: '#d93025', borderRadius: 999, padding: '2px 8px' },
  tableWrap: { overflowX: 'auto' },
  table: { borderCollapse: 'collapse', width: '100%', fontSize: 12.5 },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #dadce0', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '6px 8px', borderBottom: '1px solid #f1f3f4', color: '#202124', whiteSpace: 'nowrap', verticalAlign: 'top' },
  tdWrap: { whiteSpace: 'normal', maxWidth: 260, wordBreak: 'break-word', color: '#5f6368' },

  auditRow: { display: 'flex', gap: 10, fontSize: 12.5, padding: '5px 0', borderTop: '1px solid #f1f3f4', flexWrap: 'wrap' },
  auditTime: { color: '#5f6368', whiteSpace: 'nowrap', minWidth: 150 },
  auditWho: { fontWeight: 700, minWidth: 70 },
  auditOp: { color: '#1a73e8', fontWeight: 700, minWidth: 90 },
  auditTitle: { color: '#202124', flex: 1, minWidth: 160 },
};
